import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Popover from "@mui/material/Popover"
import Tooltip from "@mui/material/Tooltip"
import { Var } from "../icons/Var"
import { Control } from "./Control"
import { useState } from "react"
import { Parameter } from "../../shared/Parameter"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { ParameterChip } from "./ParameterChip"
import Clear from "@mui/icons-material/Clear"
import { useEditor } from "tldraw"
import { ExpressionInput } from "../pages/ExpressionInput"
import { Expr } from "../../shared/Expr"
import { Dialog } from "@mui/material"
import { LDPage } from "../../shared/LDPage"
import { createParameter } from "../createParameter"

export const ParamDefaults: Record<string, string | number | boolean> = {
  color: "#563503ff",
  number: 1,
  boolean: true,
  string: "",
}

type ParameterizableControlProps = {
  title: string
  icon?: React.ReactElement
  children: React.ReactNode
  type: Parameter["type"]

  // TODO(alvaro): should these just be replace with `expr`?
  value: string | null
  bound: boolean
  expr: Expr<any>
  // ----

  parameterTitle?: string
  onChange: (expr: Expr<any> | null) => void
}

export function ParameterizableControl({
  title,
  icon,
  children,
  type,
  value,
  bound,
  onChange,
  expr,
  parameterTitle = "Parameter: none",
}: ParameterizableControlProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const editor = useEditor()
  const page = editor.getCurrentPage() as LDPage

  const [expressionOpen, setExpressionOpen] = useState(false)

  const parameters: Record<string, Parameter> = Object.fromEntries(
    Object.entries(page.meta.parameters).filter(([id, p]) => p.type === type),
  )

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", flex: 1 }}>
      <div style={{ display: "flex", flex: 1, alignItems: "center" }}>
        <Control icon={icon} title={title}>
          {children}
        </Control>
      </div>
      <div>
        <IconButton
          size="small"
          style={{
            border: "0.7px solid rgba(0, 0, 0, 0.2)",
            padding: 3,
            ...(bound ? { background: "#9cf" } : {}),
          }}
          onClick={handleClick}
        >
          <Tooltip title={parameterTitle}>
            <Var size={12} />
          </Tooltip>
        </IconButton>
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          elevation={2}
        >
          <ParameterSelect
            parameters={parameters}
            type={type}
            value={value}
            onChange={(val) => {
              if (val) {
                onChange(Expr.ref(val))
              } else {
                onChange(null)
              }
            }}
            onClose={() => setAnchorEl(null)}
          />
          {/* <Button onClick={() => setExpressionOpen(true)}>
            Use Expression
          </Button> */}
        </Popover>
      </div>
      {expressionOpen && (
        <Dialog open={expressionOpen} onClose={() => setExpressionOpen(false)}>
          <ExpressionInput
            parameters={{ ...page.meta.parameters }}
            env={{ ...page.meta.parameterValues, time: Expr.lit(0) }}
            defaultExpression={expr ? expr : Expr.lit(0)}
            onSubmit={(expr) => {
              onChange(expr)
              setExpressionOpen(false)
            }}
          />
        </Dialog>
      )}
    </div>
  )
}

type ParameterSelectProps = {
  parameters: Record<string, Parameter>
  value: string | null
  onChange: (value: string | null) => void
  onClose: () => void
  type: Parameter["type"]
}

function ParameterSelect({
  value,
  onChange,
  onClose,
  type,
}: ParameterSelectProps) {
  const editor = useEditor()
  const page = useCurrentPage()
  const parameters: Record<string, Parameter> = Object.fromEntries(
    Object.entries(page.meta.parameters).filter(([id, p]) => p.type === type),
  )

  function handleNewParam() {
    createParameter(type, editor)
  }
  return (
    <Paper sx={{ padding: "6px 12px" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          alignItems: "flex-start",
        }}
      >
        {/* <CheckboxChip
          variant="blend"
          checked={value === null}
          onClick={() => onChange(null)}
        >
          None
        </CheckboxChip> */}
        {Object.entries(parameters).length === 0 && (
          <div style={{ padding: 10, fontSize: 14 }}>No Parameters</div>
        )}
        {Object.entries(parameters).map(([id, param]) => (
          <ParameterChip
            id={id}
            key={id}
            type={param.type}
            name={param.name}
            checked={value === id}
            onClick={() => onChange(id)}
            value={Expr.evaluate(page.meta.parameterValues[id], {})}
          />
        ))}
        <Button
          variant="text"
          style={{ textTransform: "none" }}
          onClick={handleNewParam}
        >
          New parameter
        </Button>
        {value && (
          <Button
            variant="outlined"
            onClick={() => {
              onChange(null)
              onClose()
            }}
            style={{ textTransform: "none" }}
            size="small"
            startIcon={<Clear fontSize="inherit" />}
          >
            Clear
          </Button>
        )}
      </div>
    </Paper>
  )
}
